import { createTheme, ThemeOptions } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

// Apple-inspired color palette - optimized for readability
const appleColors = {
  // Primary blues
  appleBlueLight: '#007AFF',
  appleBlueDark: '#0A84FF',
  
  // System grays - improved contrast
  systemGray: '#8E8E93',
  systemGray2: '#AEAEB2',
  systemGray3: '#C7C7CC',
  systemGray4: '#D1D1D6',
  systemGray5: '#E5E5EA',
  systemGray6: '#F2F2F7',
  
  // System backgrounds - better contrast
  systemBackground: '#FFFFFF',
  systemBackgroundSecondary: '#F2F2F7',
  systemBackgroundTertiary: '#FFFFFF',
  
  // Dark mode backgrounds - improved readability
  systemBackgroundDark: '#000000',
  systemBackgroundSecondaryDark: '#1C1C1E',
  systemBackgroundTertiaryDark: '#2C2C2E',
  
  // Text colors - enhanced contrast
  label: '#000000',
  labelSecondary: '#3C3C43',
  labelTertiary: '#3C3C43',
  labelQuaternary: '#3C3C43',
  
  // Dark mode text - improved readability
  labelDark: '#FFFFFF',
  labelSecondaryDark: '#EBEBF5',
  labelTertiaryDark: '#EBEBF5',
  labelQuaternaryDark: '#EBEBF5',
  
  // Semantic colors
  systemRed: '#FF3B30',
  systemGreen: '#34C759',
  systemOrange: '#FF9500',
  systemYellow: '#FFCC00',
  systemPurple: '#AF52DE',
  systemPink: '#FF2D92',
  systemTeal: '#5AC8FA',
  systemIndigo: '#5856D6',
};

// Base theme configuration with improved readability
const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: [
      '"Inter"',
      '"SF Pro Display"',
      '"Poppins"',
      '-apple-system',
      'BlinkMacSystemFont',
      'system-ui',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    
    // Improved typography scale for better readability
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    button: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '0.02em',
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0.03em',
    },
  },
  
  shape: {
    borderRadius: 12, // Reduced from 16 for subtler look
  },
  
  components: {
    // Button component - toned down glassmorphism
    MuiButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '12px 24px',
          minHeight: 44,
          fontSize: '1rem',
          transition: 'all 0.2s ease-out', // Faster transition
          
          ...(ownerState.variant === 'contained' && {
            background: theme.palette.mode === 'light'
              ? `linear-gradient(135deg, ${appleColors.appleBlueLight} 0%, ${appleColors.appleBlueLight} 100%)`
              : `linear-gradient(135deg, ${appleColors.appleBlueDark} 0%, ${appleColors.appleBlueDark} 100%)`,
            color: '#FFFFFF',
            border: 'none',
            boxShadow: theme.palette.mode === 'light'
              ? '0 2px 8px 0 rgba(0, 122, 255, 0.3)'
              : '0 2px 8px 0 rgba(10, 132, 255, 0.3)',
            
            '&:hover': {
              background: theme.palette.mode === 'light'
                ? `linear-gradient(135deg, ${appleColors.appleBlueLight}E6 0%, ${appleColors.appleBlueLight}E6 100%)`
                : `linear-gradient(135deg, ${appleColors.appleBlueDark}E6 0%, ${appleColors.appleBlueDark}E6 100%)`,
              transform: 'translateY(-1px)',
              boxShadow: theme.palette.mode === 'light'
                ? '0 4px 12px 0 rgba(0, 122, 255, 0.4)'
                : '0 4px 12px 0 rgba(10, 132, 255, 0.4)',
            },
            
            '&:active': {
              transform: 'translateY(0px)',
            },
          }),
          
          ...(ownerState.variant === 'outlined' && {
            background: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.8)' // More opaque for readability
              : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(8px)', // Reduced blur
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${theme.palette.mode === 'light' 
              ? appleColors.appleBlueLight 
              : appleColors.appleBlueDark}`,
            color: theme.palette.mode === 'light' 
              ? appleColors.appleBlueLight 
              : appleColors.appleBlueDark,
            
            '&:hover': {
              background: theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(255, 255, 255, 0.15)',
              transform: 'translateY(-1px)',
            },
          }),
        }),
      },
    },
    
    // Paper component - subtle glassmorphism
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.9)' // Higher opacity for readability
            : 'rgba(28, 28, 30, 0.9)',
          backdropFilter: 'blur(10px)', // Reduced blur
          WebkitBackdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.mode === 'light' 
            ? 'rgba(255, 255, 255, 0.3)' 
            : 'rgba(255, 255, 255, 0.1)'}`,
          boxShadow: theme.palette.mode === 'light'
            ? '0 4px 16px 0 rgba(0, 0, 0, 0.08)' // Softer shadow
            : '0 4px 16px 0 rgba(0, 0, 0, 0.3)',
          transition: 'all 0.2s ease-out',
        }),
      },
    },
    
    // Card component - optimized
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.85)' // Better opacity balance
            : 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(8px)', // Reduced for performance
          WebkitBackdropFilter: 'blur(8px)',
          border: `1px solid ${theme.palette.mode === 'light' 
            ? 'rgba(255, 255, 255, 0.4)' 
            : 'rgba(255, 255, 255, 0.12)'}`,
          borderRadius: 16,
          boxShadow: theme.palette.mode === 'light'
            ? '0 2px 12px 0 rgba(0, 0, 0, 0.06)'
            : '0 2px 12px 0 rgba(0, 0, 0, 0.25)',
          transition: 'all 0.2s ease-out',
          
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'light'
              ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
              : '0 4px 20px 0 rgba(0, 0, 0, 0.35)',
          },
        }),
      },
    },
    
    // TextField component - improved readability
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            background: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)' // High opacity for forms
              : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(6px)', // Minimal blur for inputs
            WebkitBackdropFilter: 'blur(6px)',
            borderRadius: 10,
            transition: 'all 0.2s ease-out',
            
            '& fieldset': {
              border: `1px solid ${theme.palette.mode === 'light' 
                ? 'rgba(0, 0, 0, 0.1)' 
                : 'rgba(255, 255, 255, 0.2)'}`,
            },
            
            '&:hover fieldset': {
              border: `1px solid ${theme.palette.mode === 'light' 
                ? appleColors.appleBlueLight + '80' 
                : appleColors.appleBlueDark + '80'}`,
            },
            
            '&.Mui-focused fieldset': {
              border: `2px solid ${theme.palette.mode === 'light' 
                ? appleColors.appleBlueLight 
                : appleColors.appleBlueDark}`,
            },
            
            '&.Mui-focused': {
              background: theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 1)' // Fully opaque when focused
                : 'rgba(255, 255, 255, 0.12)',
            },
          },
          
          '& .MuiInputLabel-root': {
            color: theme.palette.mode === 'light' 
              ? appleColors.labelSecondary 
              : appleColors.labelSecondaryDark,
            fontWeight: 500,
          },
          
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.mode === 'light' 
              ? appleColors.appleBlueLight 
              : appleColors.appleBlueDark,
          },
        }),
      },
    },
    
    // AppBar component - better readability
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.mode === 'light'
            ? 'rgba(255, 255, 255, 0.95)' // High opacity for navigation
            : 'rgba(28, 28, 30, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: theme.palette.mode === 'light'
            ? '0 1px 8px 0 rgba(0, 0, 0, 0.05)'
            : '0 1px 8px 0 rgba(0, 0, 0, 0.2)',
          borderBottom: `1px solid ${theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.05)' 
            : 'rgba(255, 255, 255, 0.1)'}`,
        }),
      },
    },
  },
};

// Light theme - enhanced contrast
export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: appleColors.appleBlueLight,
      dark: '#0051D5',
      light: '#4DA3FF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: appleColors.systemTeal,
      dark: '#32ADE6',
      light: '#7ED4F5',
      contrastText: '#FFFFFF',
    },
    background: {
      default: appleColors.systemBackground,
      paper: appleColors.systemBackgroundSecondary,
    },
    text: {
      primary: appleColors.label,
      secondary: appleColors.labelSecondary,
    },
    divider: appleColors.systemGray4,
    grey: {
      50: appleColors.systemGray6,
      100: appleColors.systemGray5,
      200: appleColors.systemGray4,
      300: appleColors.systemGray3,
      400: appleColors.systemGray2,
      500: appleColors.systemGray,
      600: '#6D6D80',
      700: '#48484A',
      800: '#3A3A3C',
      900: '#2C2C2E',
    },
    error: {
      main: appleColors.systemRed,
    },
    warning: {
      main: appleColors.systemOrange,
    },
    info: {
      main: appleColors.appleBlueLight,
    },
    success: {
      main: appleColors.systemGreen,
    },
  },
});

// Dark theme - improved readability
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: appleColors.appleBlueDark,
      dark: '#007AFF',
      light: '#64D2FF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#64D2FF',
      dark: appleColors.systemTeal,
      light: '#A8E6F0',
      contrastText: '#000000',
    },
    background: {
      default: appleColors.systemBackgroundDark,
      paper: appleColors.systemBackgroundSecondaryDark,
    },
    text: {
      primary: appleColors.labelDark,
      secondary: appleColors.labelSecondaryDark,
    },
    divider: '#3A3A3C',
    grey: {
      50: '#8E8E93',
      100: '#7C7C80',
      200: '#6D6D80',
      300: '#5E5E5E',
      400: '#4E4E4E',
      500: '#3E3E3E',
      600: '#2E2E2E',
      700: '#2C2C2E',
      800: '#1C1C1E',
      900: '#000000',
    },
    error: {
      main: '#FF453A',
    },
    warning: {
      main: '#FF9F0A',
    },
    info: {
      main: appleColors.appleBlueDark,
    },
    success: {
      main: '#30D158',
    },
  },
});

// Export a function to get the appropriate theme based on the mode
export const getTheme = (mode: PaletteMode): ThemeOptions => {
  return mode === 'dark' ? darkTheme : lightTheme;
};

// For backward compatibility, export light theme as default
const theme = lightTheme;
export default theme; 